import { Component, EventEmitter, inject, Input, input, OnInit, Output } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { AssessmentService } from '../../services/assessment.service';
import { MatDialog } from '@angular/material/dialog';
import { HealthConditionDialogComponent } from '../../components/health-condition-dialog/health-condition-dialog.component';
import { Person } from '../../data/person';
import { ApplicantDetails } from '../../data/applicant-details';
import { IFormTypes } from '../../data/form-types';
import { HealthDetails } from '../../models/health-details';
import { HealthCondition } from '../../models/health-condition';
import { CrimeDetails } from '../../models/crime-details';
import { RefusedVisaDetails } from '../../models/refused-visa-details';
import { CrimeDetailsDialogComponent } from '../../components/crime-details-dialog/crime-details-dialog.component';
import { CancelledVisaDetails } from '../../models/cancelled-visa-details';
import { RefusedVisaDialogComponent } from '../../components/refused-visa-dialog/refused-visa-dialog.component';
import { CancelledVisaDialogComponent } from '../../components/cancelled-visa-dialog/cancelled-visa-dialog.component';
import { IFormComponent } from '../../models/i-form-component';
import { map, Subject, takeUntil } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';


interface HealthConditionRow extends HealthCondition { itemIndex: number; }
interface CrimeRow extends CrimeDetails { itemIndex: number; }
interface RefusedVisaRow extends RefusedVisaDetails { itemIndex: number; }
interface CancelledVisaRow extends CancelledVisaDetails { itemIndex: number; }

@Component({
  selector: 'app-health-character',
  templateUrl: './health-character.component.html',
  styleUrl: './health-character.component.scss'
})
export class HealthCharacterComponent implements OnInit, IFormComponent {
  @Input()model!: InitialAssessmentForm;
  @Input()formTypes!: IFormTypes;
  @Output() formReady = new EventEmitter<FormGroup>();

  private applicantPerson: Person = Object.assign(new Person(), { type: 'applicant' });

  personList: Array<Person> = new Array<Person>();
  personOptions: Array<{ value: Person, label: string }> = [];
  displayedColumnsHealth: string[] = ['name', 'condition', 'edit', 'delete'];
  displayedColumnsCrime: string[] = ['name', 'offence', 'edit', 'delete'];
  displayedColumnsVisa: string[] = ['name', 'refused', 'edit', 'delete'];
  displayedColumnsCancelledVisa: string[] = ['name', 'cancelled', 'edit', 'delete'];
  nextClicked: boolean = false;

  formBuilder = inject(NonNullableFormBuilder);
  healthCharacterForm = this.formBuilder.group({
    anyHealthConditions: ['', Validators.required],
    anyCrime: ['', Validators.required],
    refusedVisa: ['', Validators.required],
    cancelledVisa: ['', Validators.required]
  },
{
      validators: requiredListWhenYesValidator([
        {
          controlName: 'anyHealthConditions',
          errorKey: 'healthConditionRequired',
          getItems: () => this.safeHealthConditionList
        },
        {
          controlName: 'anyCrime',
          errorKey: 'crimeRequired',
          getItems: () => this.safeCrimeList
        },
        {
          controlName: 'refusedVisa',
          errorKey: 'refusedVisaRequired',
          getItems: () => this.safeRefusedVisaList
        },
        {
          controlName: 'cancelledVisa',
          errorKey: 'cancelledVisaRequired',
          getItems: () => this.safeCancelledVisaList
        }
      ])
    });

      private destroy$ = new Subject<void>();
      private breakpointObserver = inject(BreakpointObserver);
      isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
        map(result => result.matches),
        takeUntil(this.destroy$)
      );

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }


  constructor(private dialog: MatDialog) { }

  ngOnInit(): void {
    this.model = this.model || new InitialAssessmentForm();
    this.model.applicantDetails = this.model.applicantDetails || new ApplicantDetails();
    this.model.health = this.model.health || new HealthDetails();
    this.personList = this.model.applicantDetails.personList || [];
    this.model.applicantDetails.healthCondition = this.model.applicantDetails.healthCondition || [];
    this.populatePersonOptions();

    this.healthCharacterForm.patchValue(this.model.health);

  this.healthCharacterForm?.valueChanges.subscribe(value => {
      this.model.health = {
        ...this.model.health!,
        ...value
      }
      this.nextClicked = false;
      this.healthCharacterForm.updateValueAndValidity({ emitEvent: false });
    });

    this.formReady.emit(this.healthCharacterForm);
  }

  populatePersonOptions(): void {

    this.applicantPerson.firstName = this.model.applicantDetails?.firstName || 'The';
    this.applicantPerson.surname = this.model.applicantDetails?.surname || 'Applicant';
    this.applicantPerson.dob = this.model.applicantDetails?.dob || '';

    const tempPersonList = [this.applicantPerson, ...this.personList];

    this.personOptions = tempPersonList.map(person => ({
      value: person,
      label: `${person.firstName} ${person.surname}`.trim()
    }));
  }

  private isApplicantPerson(person: Person): boolean {
    return person.type === 'applicant';
  }

  private resolvePersonListEntry(person: Person): Person | null {
    return this.model?.applicantDetails?.personList?.find(p =>
      p.firstName === person.firstName && p.surname === person.surname && p.dob === person.dob
    ) ?? null;
  }

  get safeHealthConditionList(): HealthConditionRow[] {
    const applicantDetails = this.model?.applicantDetails;
    if (!applicantDetails) {
      return [];
    }

    const rows: HealthConditionRow[] = (applicantDetails.healthCondition ?? []).map((condition, itemIndex) =>
      ({ condition, person: this.applicantPerson, itemIndex }));

    (applicantDetails.personList ?? []).forEach(person => {
      (person.healthCondition ?? []).forEach((condition, itemIndex) => {
        rows.push({ condition, person, itemIndex });
      });
    });

    return rows;
  }

  get safeCrimeList(): CrimeRow[] {
    const applicantDetails = this.model?.applicantDetails;
    if (!applicantDetails) {
      return [];
    }

    const rows: CrimeRow[] = (applicantDetails.applicantCrime ?? []).map((offence, itemIndex) =>
      ({ offence, person: this.applicantPerson, itemIndex }));

    (applicantDetails.personList ?? []).forEach(person => {
      (person.applicantCrime ?? []).forEach((offence, itemIndex) => {
        rows.push({ offence, person, itemIndex });
      });
    });

    return rows;
  }

  get safeRefusedVisaList(): RefusedVisaRow[] {
    const applicantDetails = this.model?.applicantDetails;
    if (!applicantDetails) {
      return [];
    }

    const rows: RefusedVisaRow[] = (applicantDetails.refusedVisa ?? []).map((refused, itemIndex) =>
      ({ refused, person: this.applicantPerson, itemIndex }));

    (applicantDetails.personList ?? []).forEach(person => {
      (person.refusedVisa ?? []).forEach((refused, itemIndex) => {
        rows.push({ refused, person, itemIndex });
      });
    });

    return rows;
  }

  get safeCancelledVisaList(): CancelledVisaRow[] {
    const applicantDetails = this.model?.applicantDetails;
    if (!applicantDetails) {
      return [];
    }

    const rows: CancelledVisaRow[] = (applicantDetails.cancelledVisa ?? []).map((cancelled, itemIndex) =>
      ({ cancelled, person: this.applicantPerson, itemIndex }));

    (applicantDetails.personList ?? []).forEach(person => {
      (person.cancelledVisa ?? []).forEach((cancelled, itemIndex) => {
        rows.push({ cancelled, person, itemIndex });
      });
    });

    return rows;
  }

  private addHealthConditionValue(person: Person, value: string): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      applicantDetails.healthCondition = [...applicantDetails.healthCondition, value];
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      owner.healthCondition = [...owner.healthCondition, value];
    }
  }

  private removeHealthConditionValue(person: Person, itemIndex: number): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      const values = [...applicantDetails.healthCondition];
      values.splice(itemIndex, 1);
      applicantDetails.healthCondition = values;
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      const values = [...owner.healthCondition];
      values.splice(itemIndex, 1);
      owner.healthCondition = values;
    }
  }

  private addCrimeValue(person: Person, value: string): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      applicantDetails.applicantCrime = [...applicantDetails.applicantCrime, value];
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      owner.applicantCrime = [...owner.applicantCrime, value];
    }
  }

  private removeCrimeValue(person: Person, itemIndex: number): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      const values = [...applicantDetails.applicantCrime];
      values.splice(itemIndex, 1);
      applicantDetails.applicantCrime = values;
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      const values = [...owner.applicantCrime];
      values.splice(itemIndex, 1);
      owner.applicantCrime = values;
    }
  }

  private addRefusedVisaValue(person: Person, value: string): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      applicantDetails.refusedVisa = [...applicantDetails.refusedVisa, value];
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      owner.refusedVisa = [...owner.refusedVisa, value];
    }
  }

  private removeRefusedVisaValue(person: Person, itemIndex: number): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      const values = [...applicantDetails.refusedVisa];
      values.splice(itemIndex, 1);
      applicantDetails.refusedVisa = values;
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      const values = [...owner.refusedVisa];
      values.splice(itemIndex, 1);
      owner.refusedVisa = values;
    }
  }

  private addCancelledVisaValue(person: Person, value: string): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      applicantDetails.cancelledVisa = [...applicantDetails.cancelledVisa, value];
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      owner.cancelledVisa = [...owner.cancelledVisa, value];
    }
  }

  private removeCancelledVisaValue(person: Person, itemIndex: number): void {
    const applicantDetails = this.model.applicantDetails!;
    if (this.isApplicantPerson(person)) {
      const values = [...applicantDetails.cancelledVisa];
      values.splice(itemIndex, 1);
      applicantDetails.cancelledVisa = values;
      return;
    }
    const owner = this.resolvePersonListEntry(person);
    if (owner) {
      const values = [...owner.cancelledVisa];
      values.splice(itemIndex, 1);
      owner.cancelledVisa = values;
    }
  }

  private updateHasHealthCondition(): void {
    this.model.applicantDetails!.hasHealthCondition = this.safeHealthConditionList.length > 0;
  }

  private updateHasApplicantCrime(): void {
    this.model.applicantDetails!.hasApplicantCrime = this.safeCrimeList.length > 0;
  }

  addHealthCondition() {

    const dialogRef = this.dialog.open(HealthConditionDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { healthCondition: { condition: '', person: null }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addHealthConditionValue(result.person, result.condition);
        this.updateHasHealthCondition();
        this.healthCharacterForm.controls.anyHealthConditions.updateValueAndValidity({ emitEvent: false });
      }
    });

  }

  removeHealthCondition(index: number) {

    const row = this.safeHealthConditionList[index];
    if (!row) {
      return;
    }

    this.removeHealthConditionValue(row.person, row.itemIndex);
    this.updateHasHealthCondition();
    this.healthCharacterForm.controls.anyHealthConditions.updateValueAndValidity({ emitEvent: false });
   }

   editHealthCondition(index: number) {

    const row = this.safeHealthConditionList[index];
    if (!row) {
      return;
    }

    const dialogRef = this.dialog.open(HealthConditionDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { healthCondition: { condition: row.condition, person: row.person }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeHealthConditionValue(row.person, row.itemIndex);
        this.addHealthConditionValue(result.person, result.condition);
        this.updateHasHealthCondition();
        this.healthCharacterForm.controls.anyHealthConditions.updateValueAndValidity({ emitEvent: false });
      }
    });

  }

  addCrime() {
    const dialogRef = this.dialog.open(CrimeDetailsDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { crimeDetails: { offence: '', person: null }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addCrimeValue(result.person, result.offence);
        this.updateHasApplicantCrime();
        this.healthCharacterForm.controls.anyCrime.updateValueAndValidity({ emitEvent: false });

      }
    });
  }

  removeCrime(index: number) {
    const row = this.safeCrimeList[index];
    if (!row) {
      return;
    }

    this.removeCrimeValue(row.person, row.itemIndex);
    this.updateHasApplicantCrime();
    this.healthCharacterForm.controls.anyCrime.updateValueAndValidity({ emitEvent: false });
  }

  editCrime(index: number) {
    const row = this.safeCrimeList[index];
    if (!row) {
      return;
    }

    const dialogRef = this.dialog.open(CrimeDetailsDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { crimeDetails: { offence: row.offence, person: row.person }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeCrimeValue(row.person, row.itemIndex);
        this.addCrimeValue(result.person, result.offence);
        this.updateHasApplicantCrime();
        this.healthCharacterForm.controls.anyCrime.updateValueAndValidity({ emitEvent: false });
      }
    });

  }

  removeVisaRefusal(index: number) {
    const row = this.safeRefusedVisaList[index];
    if (!row) {
      return;
    }

    this.removeRefusedVisaValue(row.person, row.itemIndex);
    this.healthCharacterForm.controls.refusedVisa.updateValueAndValidity({ emitEvent: false });
  }
  editVisaRefusal(index: number) {
    const row = this.safeRefusedVisaList[index];
    if (!row) {
      return;
    }

    const dialogRef = this.dialog.open(RefusedVisaDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { refusedVisaDetails: { refused: row.refused, person: row.person }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeRefusedVisaValue(row.person, row.itemIndex);
        this.addRefusedVisaValue(result.person, result.refused);
        this.healthCharacterForm.controls.refusedVisa.updateValueAndValidity({ emitEvent: false });
      }
    });
  }
  addVisaRefusal() {
      const dialogRef = this.dialog.open(RefusedVisaDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { refusedVisaDetails: { refused: '', person: null }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addRefusedVisaValue(result.person, result.refused);
        this.healthCharacterForm.controls.refusedVisa.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeVisaCancellation(index: number) {
    const row = this.safeCancelledVisaList[index];
    if (!row) {
      return;
    }

    this.removeCancelledVisaValue(row.person, row.itemIndex);
    this.healthCharacterForm.controls.cancelledVisa.updateValueAndValidity({ emitEvent: false });
  }
  addVisaCancellation() {
    const dialogRef = this.dialog.open(CancelledVisaDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { cancelledVisaDetails: { cancelled: '', person: null }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addCancelledVisaValue(result.person, result.cancelled);
        this.healthCharacterForm.controls.cancelledVisa.updateValueAndValidity({ emitEvent: false });
      }
    });
  }
  editVisaCancellation(index: number) {
    const row = this.safeCancelledVisaList[index];
    if (!row) {
      return;
    }

    const dialogRef = this.dialog.open(CancelledVisaDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { cancelledVisaDetails: { cancelled: row.cancelled, person: row.person }, personOptions: this.personOptions }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeCancelledVisaValue(row.person, row.itemIndex);
        this.addCancelledVisaValue(result.person, result.cancelled);
        this.healthCharacterForm.controls.cancelledVisa.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  isFormValid(): boolean {
    this.nextClicked = true;
    this.healthCharacterForm.updateValueAndValidity({ emitEvent: false });
    return this.healthCharacterForm.valid;
  }

  markFormAsTouched(): void {
    this.healthCharacterForm.markAllAsTouched();
  }

}
